import { useEffect, type ReactNode } from "react";
import { useAnchorAdOffset } from "@/hooks/useAnchorAdOffset";

/** Number of bars mounted right now; <html data-sticky-bar> is set while there is at least one (index.css pads the page end). */
let mountedBars = 0;
export const STICKY_BAR_ATTR = "data-sticky-bar";

/**
 * The pinned bar at the bottom of a tool on phones (Merge / Split / Compress / Download and similar). It is hidden
 * from 640px up, where the normal in-page buttons are used.
 *
 * Ads stay on: the bar is lifted by the measured height of Google's anchor ad (--anchor-h, see lib/anchorAd.ts) so
 * the two never overlap. While a bar is mounted <html data-sticky-bar> is set and index.css adds bottom padding to the
 * page (phones only), so the last row of the tool and the footer can always be scrolled clear of the bar. The padding
 * sits below the content, so nothing visible moves when it appears.
 */
export function StickyActionBar({ children, className = "" }: { children: ReactNode; className?: string }) {
  useAnchorAdOffset();
  useEffect(() => {
    mountedBars += 1;
    document.documentElement.setAttribute(STICKY_BAR_ATTR, "");
    return () => {
      mountedBars -= 1;
      if (mountedBars <= 0) document.documentElement.removeAttribute(STICKY_BAR_ATTR);
    };
  }, []);
  return (
    <div
      data-testid="sticky-action-bar"
      role="region"
      aria-label="Actions"
      className={`sticky-action-bar fixed inset-x-0 z-50 flex items-center gap-2.5 border-t border-border bg-card px-4 py-2.5 sm:hidden ${className}`}
      style={{ bottom: "var(--anchor-h, 0px)" }}
    >
      {children}
    </div>
  );
}

/** Same look as the primary button in the bar: 48px tall, wraps instead of overflowing. */
export const STICKY_BAR_PRIMARY_CLASS = "min-h-12 shrink-0 whitespace-normal px-5 font-bold";
